import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';

import {
  Box,
  Container,
  Typography,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';

import { formatDate } from '../../utils/date';
import IconWarningRectangle from '../../assets/icons/icon_warning_rectangle.svg';

import Greeting from '../../components/Greeting';
import PageTitle from '../../components/PageTitle';
import AssemblyStatusCard from '../../components/AssemblyStatusCard';
import ScheduleButton from '../../components/ScheduleButton';
import ScheduleLink from '../../components/ScheduleLink';
import ScheduleModal from '../../components/ScheduleModal';
import ModalError from '../../components/ModalError';
import Loading from '../../components/Loading';

import SchedulingStore from '../../stores/SchedulingStore';
import RouterStore from '../../stores/RouterStore';
import ModalStore from '../../stores/ModalStore';

const trackAssemblyStatusStyles = ({ breakpoints }) => ({
  boxButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  schedulingDetailsBox: {
    marginTop: '8px',
    '& .MuiGrid-item': {
      padding: '0 16px 0 0',
      [breakpoints.down('xs')]: {
        padding: '16px 0',
      },
    },
  },
  boxImage: {
    paddingRight: '16px',
    [breakpoints.down('xs')]: {
      paddingRight: 0,
    },
    '& img': {
      width: '100%',
    },
  },
  boxLoading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 0',
    '& p': {
      fontSize: '16px',
      textAlign: 'center',
    },
  },
  boxAlert: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 8px',
    marginBottom: '24px',
    fontWeight: 'normal',
    width: '100%',
    background: '#e8f4fd',
  },
  boxButtonsModalConfirm: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  boxButtonsModalError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    [breakpoints.down('xs')]: {
      justifyContent: 'center',
    },
    marginTop: '40px',
    '& a': {
      marginRight: '40px',
    },
  },
});

class TrackAssemblyStatus extends Component {
  async componentDidMount() {
    const { schedulingStore, match } = this.props;
    const { getScheduleDetails } = schedulingStore;
    const { uuid } = match.params;

    await getScheduleDetails(uuid);
  }

  handleRescheduleClick = uuid => {
    const { routerStore } = this.props;
    const { push } = routerStore;

    push(`/agendar/${uuid}`);
  };

  handleCancelClick = event => {
    event.preventDefault();

    const { modalStore } = this.props;
    const { openModal } = modalStore;

    openModal();
  };

  handleConfirmCancelClick = async event => {
    if (event) event.preventDefault();

    const { schedulingStore, modalStore } = this.props;
    const { closeModal } = modalStore;
    const {
      data: { customer, schedules },
      cancelScheduleAssembly,
    } = schedulingStore;

    const schedule = schedules.length > 0 && schedules[0];

    closeModal();
    await cancelScheduleAssembly(
      schedule.uuid,
      schedule.integrationId,
      customer.name
    );
  };

  handleNoCancelClick = event => {
    event.preventDefault();

    const { modalStore } = this.props;
    const { closeModal } = modalStore;

    closeModal();
  };

  render() {
    const { classes, schedulingStore } = this.props;
    const {
      isLoading,
      isFailure,
      data: { customer, schedules },
    } = schedulingStore;

    const schedule = schedules.length > 0 && schedules[0];
    const { product } = schedule;

    return (
      <>
        {isLoading && <Loading />}

        <Box mt={5} mb={12}>
          <Container>
            <Greeting name={customer.name} />
            <PageTitle title="Acompanhar montagem" />
            <>
              <Box>
                <Typography component="div">
                  <Box fontSize={21} fontWeight="bold" mt={5}>
                    Situação da solicitação de montagem
                  </Box>
                </Typography>
                <Box mt={3} mb={5}>
                  {schedule && schedule.scheduledDate && !schedule.history && (
                    <AssemblyStatusCard
                      type="success"
                      reason="Montagem agendada"
                      description={`A montagem foi agendada com sucesso e será realizada em ${formatDate(
                        schedule.scheduledDate,
                        'DD/MM'
                      )} (${formatDate(schedule.scheduledDate, 'dddd')})`}
                    />
                  )}
                  {schedule &&
                    schedule.history &&
                    schedule.history.id === 4 && (
                      <AssemblyStatusCard
                        type="error"
                        reason="Montagem cancelada"
                        description="A montagem foi cancelada."
                      />
                    )}
                  {schedule &&
                    schedule.history &&
                    schedule.history.id === 5 && (
                      <AssemblyStatusCard
                        type="warning"
                        reason="Cliente ausente"
                        description="A montagem não foi realizada, pois o cliente estava ausente."
                      />
                    )}
                  {schedule &&
                    schedule.history &&
                    schedule.history.id === 6 && (
                      <AssemblyStatusCard
                        type="warning"
                        reason="Montagem não realizada"
                        description="A montagem não foi realizada."
                      />
                    )}
                </Box>
                {schedule && schedule.isCancelable && (
                  <Box className={classes.boxButtons}>
                    <ScheduleButton
                      onClick={() => this.handleRescheduleClick(schedule.uuid)}
                    >
                      Reagendar
                    </ScheduleButton>
                    <ScheduleLink onClick={this.handleCancelClick}>
                      Cancelar agendamento
                    </ScheduleLink>
                  </Box>
                )}
              </Box>
              <Box mt={5}>
                <Typography component="div">
                  <Box fontSize={21} fontWeight="bold">
                    Detalhes do agendamento
                  </Box>
                  {!product ? (
                    <Box className={classes.boxLoading} mb={5}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid container className={classes.schedulingDetailsBox}>
                      {product.imageLink && product.description && (
                        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                          <Box className={classes.boxImage}>
                            <img
                              src={product.imageLink.replace(
                                '{w}x{h}',
                                '540x540'
                              )}
                              alt={product.description}
                            />
                          </Box>
                        </Grid>
                      )}
                      {schedule.scheduledDate && (
                        <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                          <Box fontSize={16}>
                            <Typography component="div">
                              <Box fontSize={16} fontWeight="bold">
                                Data do agendamento:
                              </Box>
                              <Box fontSize={16}>
                                {formatDate(schedule.scheduledDate, 'L')}
                              </Box>
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                        {product.description && (
                          <Box fontSize={16}>
                            <Typography component="div">
                              <Box fontSize={16} fontWeight="bold">
                                Produto:
                              </Box>
                              <Box fontSize={16}>{product.description}</Box>
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                        {customer.address && (
                          <Box fontSize={16}>
                            <Typography component="div">
                              <Box fontSize={16} fontWeight="bold">
                                Endereço da montagem:
                              </Box>
                              <Box fontSize={16}>{customer.address}</Box>
                            </Typography>
                          </Box>
                        )}
                        {customer.cellphone && (
                          <Box fontSize={16} mt={2}>
                            <Typography component="div">
                              <Box fontSize={16} fontWeight="bold">
                                Telefone:
                              </Box>
                              <Box fontSize={16}>{customer.cellphone}</Box>
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Typography>
              </Box>
            </>
          </Container>
        </Box>
        <ScheduleModal maxWidth="xs" title="Confirmação">
          <Box className={classes.modalBody}>
            <Box mb={1}>
              <img src={IconWarningRectangle} alt="Ícone de alerta" />
            </Box>
            <Box px={2} mb={3}>
              <Typography>
                <strong>Deseja realmente cancelar o agendamento?</strong>
              </Typography>
            </Box>
            <Box mb={3} className={classes.boxAlert}>
              <Typography>
                <strong>Atenção:</strong> caso você confirme a ação, o
                agendamento da sua montagem será cancelado, não sendo possível
                agendar novamente por esse canal.
              </Typography>
            </Box>
            <Box my={2} className={classes.boxButtonsModalConfirm}>
              <ScheduleLink onClick={this.handleConfirmCancelClick}>
                Sim
              </ScheduleLink>
              <ScheduleLink onClick={this.handleNoCancelClick}>
                Não
              </ScheduleLink>
            </Box>
          </Box>
        </ScheduleModal>
        {isFailure && (
          <ModalError title="Erro">
            <Box className={classes.boxButtonsModalError}>
              <ScheduleButton onClick={() => this.handleConfirmCancelClick()}>
                Tentar cancelar novamente
              </ScheduleButton>
            </Box>
          </ModalError>
        )}
      </>
    );
  }
}

TrackAssemblyStatus.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
    }),
  }).isRequired,
  schedulingStore: PropTypes.instanceOf(SchedulingStore).isRequired,
  routerStore: PropTypes.instanceOf(RouterStore).isRequired,
  modalStore: PropTypes.instanceOf(ModalStore).isRequired,
};

export default withStyles(trackAssemblyStatusStyles)(
  inject(
    'schedulingStore',
    'routerStore',
    'modalStore'
  )(observer(TrackAssemblyStatus))
);
